import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { api, ApiError } from "../../lib/api.js";
import { LoadingSpinner } from "../../components/LoadingSpinner.js";
import { useT, type Translate } from "../../i18n/index.js";
import { QuestionForm } from "./QuestionForm.js";
import type { QuestionFormInitialValues } from "./QuestionForm.js";
import type { Question, QuizDraft, QuizWithQuestions } from "./types.js";
import type { QuestionType } from "./types.js";
import {
  accentRule,
  badge,
  dangerButton,
  errorText,
  ghostButton,
  primaryButton,
  surface,
  textInput,
} from "./quiz-ui.js";

type LoadState =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "ready"; quiz: QuizWithQuestions };

function describeType(type: string, t: Translate): string {
  const labels: Record<QuestionType, string> = {
    quiz_single: t("quiz.typeSingle"),
    quiz_multi: t("quiz.typeMultiple"),
    true_false: t("quiz.typeTrueFalse"),
    type_answer: t("quiz.typeAnswer"),
    slide: t("quiz.typeSlide"),
  };
  return labels[type as QuestionType] ?? type;
}

export function QuizEditor() {
  const { quizId } = useParams<{ quizId: string }>();
  const navigate = useNavigate();
  const t = useT();
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [title, setTitle] = useState("");
  const [titleStatus, setTitleStatus] = useState<"idle" | "saving" | "saved">("idle");
  const [actionError, setActionError] = useState<string | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingQuestionId, setEditingQuestionId] = useState<string | null>(null);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isHosting, setIsHosting] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const loadQuiz = useCallback(async () => {
    if (!quizId) return;
    setState({ kind: "loading" });
    try {
      const data = await api.get<{ quiz: QuizWithQuestions }>(`/quizzes/${quizId}`);
      setState({ kind: "ready", quiz: data.quiz });
      setTitle(data.quiz.title);
    } catch (err) {
      setState({
        kind: "error",
        message: err instanceof ApiError ? err.message : t("quiz.loadThisFailed"),
      });
    }
  }, [quizId, t]);

  useEffect(() => {
    void loadQuiz();
  }, [loadQuiz]);

  const applyReorder = async (reordered: Question[]) => {
    if (!quizId || state.kind !== "ready") return;
    setState({ kind: "ready", quiz: { ...state.quiz, questions: reordered } });
    try {
      await api.put(`/quizzes/${quizId}/reorder`, {
        orderedIds: reordered.map((q) => q.id),
      });
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : t("quiz.reorderFailed"));
      void loadQuiz();
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || state.kind !== "ready") return;
    const questions = state.quiz.questions;
    const oldIndex = questions.findIndex((q) => q.id === active.id);
    const newIndex = questions.findIndex((q) => q.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    void applyReorder(arrayMove(questions, oldIndex, newIndex));
  };

  const handleMoveQuestion = (questionId: string, direction: "up" | "down") => {
    if (state.kind !== "ready") return;
    const questions = state.quiz.questions;
    const index = questions.findIndex((q) => q.id === questionId);
    if (index < 0) return;
    const swapIndex = direction === "up" ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= questions.length) return;
    const reordered = [...questions];
    const temp = reordered[index]!;
    reordered[index] = reordered[swapIndex]!;
    reordered[swapIndex] = temp;
    void applyReorder(reordered);
  };

  const handleSaveTitle = async () => {
    if (!quizId || state.kind !== "ready") return;
    const trimmed = title.trim();
    if (trimmed.length === 0) {
      setActionError(t("quiz.titleRequired"));
      return;
    }
    if (trimmed === state.quiz.title) {
      setTitleStatus("saved");
      return;
    }
    setActionError(null);
    setTitleStatus("saving");
    try {
      await api.patch(`/quizzes/${quizId}`, { title: trimmed });
      setState({ kind: "ready", quiz: { ...state.quiz, title: trimmed } });
      setTitleStatus("saved");
    } catch (err) {
      setTitleStatus("idle");
      setActionError(err instanceof ApiError ? err.message : t("quiz.saveTitleFailed"));
    }
  };

  const handlePublishToggle = async () => {
    if (!quizId || state.kind !== "ready") return;
    const publish = !state.quiz.isPublished;
    setActionError(null);
    setIsPublishing(true);
    try {
      if (publish) {
        await api.post(`/quizzes/${quizId}/publish`);
      } else {
        await api.delete(`/quizzes/${quizId}/publish`);
      }
      setState({ kind: "ready", quiz: { ...state.quiz, isPublished: publish } });
    } catch (err) {
      if (err instanceof ApiError) {
        const detail = publish
          ? `${err.message} Add questions before publishing.`
          : err.message;
        setActionError(detail);
      } else {
        setActionError(t("quiz.publishStatusFailed"));
      }
    } finally {
      setIsPublishing(false);
    }
  };

  const handleHostSession = async () => {
    if (!quizId) return;
    setActionError(null);
    setIsHosting(true);
    try {
      const data = await api.post<{ sessionId: string; pin: string }>(
        `/quizzes/${quizId}/sessions`,
      );
      navigate(`/host/${data.sessionId}`);
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : t("quiz.startSessionFailed"));
    } finally {
      setIsHosting(false);
    }
  };

  const handleAddQuestion = async (draft: QuizDraft) => {
    if (!quizId || state.kind !== "ready") return;
    setActionError(null);
    const created = await api
      .post<{ question: Question }>(`/quizzes/${quizId}/questions`, {
        type: draft.type,
        prompt: draft.prompt ?? "",
        timerSeconds: draft.timerSeconds,
        ...(draft.imageAssetId ? { imageAssetId: draft.imageAssetId } : {}),
      })
      .catch((err: unknown) => {
        setActionError(err instanceof ApiError ? err.message : t("quiz.addQuestionFailed"));
        return null;
      });
    if (!created) return;

    try {
      await api.put<void>(`/quizzes/${quizId}/questions/${created.question.id}/options`, {
        options: draft.options,
        ...(draft.acceptedAnswers ? { acceptedAnswers: draft.acceptedAnswers } : {}),
      });
      await loadQuiz();
      setIsFormOpen(false);
    } catch (err) {
      // Adding a question is two calls. If the second fails, the first has
      // already left a question with no answers behind — one that silently
      // blocks publishing later. Take it back out so the failure the editor
      // reports is the whole story.
      await api.delete(`/quizzes/${quizId}/questions/${created.question.id}`).catch(() => {});
      setActionError(err instanceof ApiError ? err.message : t("quiz.addQuestionFailed"));
    }
  };

  const handleDeleteQuestion = async (questionId: string) => {
    if (!quizId || state.kind !== "ready") return;
    setActionError(null);
    try {
      await api.delete(`/quizzes/${quizId}/questions/${questionId}`);
      setState({
        kind: "ready",
        quiz: {
          ...state.quiz,
          questions: state.quiz.questions.filter((q) => q.id !== questionId),
        },
      });
    } catch (err) {
      setActionError(
        err instanceof ApiError ? err.message : t("quiz.deleteQuestionFailed"),
      );
    }
  };

  const handleEditQuestion = async (questionId: string, draft: QuizDraft) => {
    if (!quizId || state.kind !== "ready") return;
    setActionError(null);
    try {
      await api.patch(`/quizzes/${quizId}/questions/${questionId}`, {
        prompt: draft.prompt ?? "",
        timerSeconds: draft.timerSeconds,
        ...(draft.imageAssetId !== undefined ? { imageAssetId: draft.imageAssetId } : {}),
      });
      await api.put(`/quizzes/${quizId}/questions/${questionId}/options`, {
        options: draft.options,
        ...(draft.acceptedAnswers ? { acceptedAnswers: draft.acceptedAnswers } : {}),
      });
      await loadQuiz();
      setEditingQuestionId(null);
    } catch (err) {
      setActionError(
        err instanceof ApiError ? err.message : t("quiz.saveQuestionFailed"),
      );
    }
  };

  if (state.kind === "loading") {
    return (
      <main className="quiz-editor-page">
        <LoadingSpinner label={t("editor.loadingQuiz")} />
      </main>
    );
  }

  if (state.kind === "error") {
    return (
      <main className="quiz-editor-page">
        <div
          role="alert"
          className="quiz-editor-error-card"
          style={surface}
        >
          <p style={errorText}>{state.message}</p>
          <div className="quiz-editor-button-row">
            <button type="button" onClick={() => void loadQuiz()} style={ghostButton}>
              {t("common.tryAgain")}
            </button>
            <Link to="/admin/quizzes" style={{ ...ghostButton, textDecoration: "none" }}>
              {t("quiz.backToQuizzes")}
            </Link>
          </div>
        </div>
      </main>
    );
  }

  const { quiz } = state;
  const isPublished = quiz.isPublished;

  return (
    <main className="quiz-editor-page">
      <Link
        to="/admin/quizzes"
        className="quiz-editor-back-link"
      >
        &lsaquo; {t("quiz.allQuizzes")}
      </Link>

      <hr style={accentRule} />

      <header className="quiz-editor-header">
        <div className="quiz-editor-title-meta">
          <label htmlFor="quiz-title" className="quiz-editor-label">
            {t("quiz.title")}
          </label>
          <span style={badge(isPublished)}>{isPublished ? "published" : "draft"}</span>
        </div>
        <div className="quiz-editor-title-row">
          <input
            id="quiz-title"
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              setTitleStatus("idle");
            }}
            onBlur={() => void handleSaveTitle()}
            className="quiz-editor-title-input"
            style={textInput}
          />
          <button
            type="button"
            onClick={() => void handleSaveTitle()}
            disabled={titleStatus === "saving"}
            style={ghostButton}
          >
            {titleStatus === "saving" ? t("quiz.saving") : t("quiz.save")}
          </button>
          {isPublished && (
            <button
              type="button"
              className="btn-tactile"
              onClick={() => void handleHostSession()}
              disabled={isHosting}
              aria-label={t("quiz.hostThisQuiz")}
              style={{ ...primaryButton, opacity: isHosting ? 0.7 : 1 }}
            >
              {isHosting ? t("quiz.hosting") : t("quiz.host")}
            </button>
          )}
          <button
            type="button"
            className="btn-tactile"
            onClick={() => void handlePublishToggle()}
            disabled={isPublishing}
            style={{ ...(isPublished ? ghostButton : primaryButton), opacity: isPublishing ? 0.7 : 1 }}
          >
            {isPublishing ? t("quiz.working") : isPublished ? t("quiz.unpublish") : t("quiz.publish")}
          </button>
        </div>
        <p
          aria-live="polite"
          style={{ margin: 0, fontSize: "0.85rem", opacity: 0.7, minHeight: "1.1rem" }}
        >
          {titleStatus === "saved" ? t("quiz.titleSaved") : ""}
        </p>
      </header>

      {actionError && (
        <p role="alert" style={{ ...errorText, marginBottom: "1rem" }}>
          {actionError}
        </p>
      )}

      <section aria-labelledby="questions-heading">
        <div className="quiz-editor-section-head">
          <h2 id="questions-heading" className="quiz-editor-section-title">
            {t("quiz.questions")}
            <span style={{ opacity: 0.6, fontWeight: 400, fontSize: "1rem" }}>
              {" "}
              ({quiz.questions.length})
            </span>
          </h2>
          {!isFormOpen && (
            <button
              type="button"
              className="btn-tactile"
              onClick={() => setIsFormOpen(true)}
              style={primaryButton}
            >
              {t("quiz.addQuestion")}
            </button>
          )}
        </div>

        {quiz.questions.length === 0 && !isFormOpen && (
          <div
            className="quiz-editor-empty-state"
            style={{
              ...surface,
            }}
          >
            <h3 style={{ margin: 0, fontSize: "1.15rem" }}>{t("quiz.noQuestions")}</h3>
            <p style={{ margin: 0, maxWidth: "24rem", opacity: 0.75 }}>
              {t("quiz.firstQuestion")}
            </p>
          </div>
        )}

        {quiz.questions.length > 0 && (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={quiz.questions.map((q) => q.id)}
              strategy={verticalListSortingStrategy}
            >
              <ol className="quiz-editor-question-list">
                {quiz.questions.map((question, index) => (
                  <SortableQuestionItem
                    key={question.id}
                    index={index}
                    question={question}
                    isFirst={index === 0}
                    isLast={index === quiz.questions.length - 1}
                    isEditing={editingQuestionId === question.id}
                    onDelete={() => void handleDeleteQuestion(question.id)}
                    onEdit={() => setEditingQuestionId(question.id)}
                    onCancelEdit={() => setEditingQuestionId(null)}
                    onSaveEdit={(draft) => handleEditQuestion(question.id, draft)}
                    onMoveUp={() => handleMoveQuestion(question.id, "up")}
                    onMoveDown={() => handleMoveQuestion(question.id, "down")}
                    t={t}
                  />
                ))}
              </ol>
            </SortableContext>
          </DndContext>
        )}

        {isFormOpen && (
          <div className="quiz-editor-form-wrap">
            <QuestionForm onSubmit={handleAddQuestion} onCancel={() => setIsFormOpen(false)} />
          </div>
        )}
      </section>
    </main>
  );
}

function questionToInitialValues(question: Question): QuestionFormInitialValues {
  return {
    type: question.type as QuestionType,
    prompt: question.prompt ?? "",
    timerSeconds: question.timerSeconds,
    options: question.answerOptions.map((o) => ({ label: o.label, isCorrect: o.isCorrect })),
    typedAnswers: question.acceptedAnswers?.map((a) => a.normalizedValue).join("\n") ?? "",
  };
}

interface SortableQuestionItemProps {
  index: number;
  question: Question;
  isFirst: boolean;
  isLast: boolean;
  isEditing: boolean;
  onDelete: () => void;
  onEdit: () => void;
  onCancelEdit: () => void;
  onSaveEdit: (draft: QuizDraft) => Promise<void>;
  onMoveUp: () => void;
  onMoveDown: () => void;
  t: Translate;
}

function SortableQuestionItem({
  index,
  question,
  isFirst,
  isLast,
  isEditing,
  onDelete,
  onEdit,
  onCancelEdit,
  onSaveEdit,
  onMoveUp,
  onMoveDown,
  t,
}: SortableQuestionItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: question.id,
  });

  const dragStyle: React.CSSProperties = {
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : undefined,
    transition,
    opacity: isDragging ? 0.4 : 1,
    position: isDragging ? "relative" : undefined,
    zIndex: isDragging ? 10 : undefined,
  };

  if (isEditing) {
    return (
      <li ref={setNodeRef} className="quiz-editor-form-item" style={dragStyle}>
        <QuestionForm
          initialValues={questionToInitialValues(question)}
          existingImageAssetId={question.imageAssetId}
          onSubmit={onSaveEdit}
          onCancel={onCancelEdit}
        />
      </li>
    );
  }

  const correct = question.answerOptions.find((o) => o.isCorrect);
  const acceptedAnswers = question.acceptedAnswers?.map((a) => a.normalizedValue) ?? [];

  return (
    <li
      ref={setNodeRef}
      className="quiz-editor-question-card"
      style={{
        ...surface,
        ...dragStyle,
      }}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        aria-label={t("quiz.dragQuestion", { number: index + 1 })}
        title={t("quiz.dragReorder")}
        className="quiz-editor-drag-button"
        style={{
          cursor: isDragging ? "grabbing" : "grab",
          touchAction: "none",
        }}
      >
        ⠿
      </button>
      <span
        aria-hidden="true"
        className="quiz-editor-question-number"
      >
        {index + 1}
      </span>
      <div className="quiz-editor-question-main">
        <span
          className="quiz-editor-question-meta"
        >
          {describeType(question.type, t)} &middot; {question.timerSeconds}s
        </span>
        <p className="quiz-editor-question-prompt">{question.prompt}</p>
        {question.imageAssetId && (
          <img
            src={`/media/${question.imageAssetId}`}
            alt={t("quiz.imageForQuestion", { number: index + 1, prompt: question.prompt ?? "" })}
            width={96}
            height={54}
            className="quiz-editor-question-thumb"
          />
        )}
        {correct && (
          <p className="quiz-editor-question-detail">
            {t("quiz.correctAnswer", { answer: correct.label })}
          </p>
        )}
        {acceptedAnswers.length > 0 && (
          <p className="quiz-editor-question-detail">
            {t("quiz.acceptedAnswersSummary", { answers: acceptedAnswers.join(", ") })}
          </p>
        )}
      </div>
      <div className="quiz-editor-move-actions">
        <button
          type="button"
          onClick={onMoveUp}
          disabled={isFirst}
          aria-label={t("quiz.moveQuestionUp", { number: index + 1 })}
          style={{
            ...ghostButton,
            opacity: isFirst ? 0.35 : 1,
          }}
        >
          ▲
        </button>
        <button
          type="button"
          onClick={onMoveDown}
          disabled={isLast}
          aria-label={t("quiz.moveQuestionDown", { number: index + 1 })}
          style={{
            ...ghostButton,
            opacity: isLast ? 0.35 : 1,
          }}
        >
          ▼
        </button>
      </div>
      <div className="quiz-editor-question-actions">
      <button
        type="button"
        onClick={onEdit}
        aria-label={t("quiz.editQuestionNumber", { number: index + 1 })}
        style={ghostButton}
      >
        {t("quiz.editQuestion")}
      </button>
      <button
        type="button"
        onClick={() => {
          if (window.confirm(t("quiz.deleteQuestionConfirm", { number: index + 1 }))) onDelete();
        }}
        aria-label={t("quiz.deleteQuestionNumber", { number: index + 1 })}
        style={dangerButton}
      >
        {t("quiz.remove")}
      </button>
      </div>
    </li>
  );
}
